import { useQuery } from '@tanstack/react-query'
import { CircleCheckBig, CircleDot, ListOrdered, MessageSquare, Plus, Zap } from 'lucide-react'
import type { ComponentType } from 'react'
import { cn } from '../../lib/cn'
import { useNachlaufTakt } from '../../lib/gelesen'
import { useMerker } from '../../lib/merker'
import { aktivitaetsfeedQuery, type Aufgabe } from '../../lib/queries'
import { AufDemSchirm } from './AufDemSchirm'
import { Geschafft } from './Geschafft'
import { Jetzt } from './Jetzt'
import { KlopftAn } from './KlopftAn'
import type { MeinTagLage } from './lage'
import { Nachrichten, nachrichtenGruppen } from './Nachrichten'
import { RuhigGestellt } from './RuhigGestellt'
import { TelefonVorlauf } from './TelefonMarkieren'
import { Vorlauf } from './Vorlauf'

/**
 * **„Mein Tag" auf dem Telefon – dieselben Zonen, eine andere Zusammenstellung**
 * ([Z-16](../../../../../docs/phasen/zusatz-16-pwa-mein-tag.md),
 * [E-349](../../../../../docs/ENTSCHEIDUNGEN.md#e-349)).
 *
 * Nils, 14.09.2026: *„Nun brauchen wir für Mein Tag und Schnelle Aufgaben eine Super Mobile
 * Ansicht! Das Ding muss total super als PWA auf dem Handy laufen."*
 *
 * ## Warum Reiter und nicht die Achse
 *
 * Am Rechner ist „Mein Tag" eine **Zeitachse** mit einer Spalte daneben
 * ([Z-12](../../../../../docs/phasen/zusatz-12-mein-tag-einfach.md)): Vorlauf → Jetzt →
 * Erledigt, rechts die Nachrichten. Das lebt davon, dass man alles **auf einmal** sieht – das
 * Nächste steht unmittelbar über dem Laufenden, und der Blick wandert.
 *
 * Auf 390 px gibt es kein „auf einmal". Untereinander gestapelt (so stand es seit Z-10) war die
 * Seite fünf Bildschirme lang, und das Wichtigste – *woran arbeite ich gerade* – lag unter dem
 * Vorlauf, also erst nach dem ersten Wischen. Wer auf dem Telefon in Work schaut, will genau
 * eine Frage beantwortet haben, und die soll ohne Wischen dastehen.
 *
 * Also **vier Reiter, einer je Frage**, am unteren Rand, wo der Daumen ist:
 *
 * | Reiter | Frage | Zahl daran |
 * |---|---|---|
 * | Jetzt | woran arbeite ich | rot, wenn etwas anklopft (Frist, Abnahme) |
 * | Vorlauf | was kommt als Nächstes | wie viel im Vorlauf steht |
 * | Erledigt | was habe ich heute weggeschafft | heute erledigt, grün |
 * | Nachrichten | hat mich jemand gemeint | ungelesen; darunter Schirm und Ruhig gestellt |
 *
 * ## Was **nicht** neu ist
 *
 * Kein Baustein. `Jetzt`, `Vorlauf`, `Geschafft`, `Nachrichten`, `AufDemSchirm`,
 * `RuhigGestellt` und `KlopftAn` sind exakt die Komponenten des Rechners – nur einzeln gezeigt
 * statt nebeneinander. Was dort gebaut, geprüft und abgenommen ist, gilt hier mit. Die eine
 * Ausnahme ist der Vorlauf: er steht hier **mit dem Nächsten oben** (`naechstesOben`), weil die
 * Begründung für „unten" – die Nachbarschaft zu „Jetzt" – in einem eigenen Reiter nicht mehr
 * gilt und die wichtigste Zeile sonst hinter fünf anderen läge.
 *
 * ## Der ⚡-Knopf
 *
 * Die Schnelle Aufgabe ([Z-15](../../../../../docs/phasen/zusatz-15-schnelle-aufgabe.md)) ist
 * auf dem Telefon die häufigste Handlung: *„Kunde X zurückrufen"*, unterwegs, in fünf Sekunden.
 * Deshalb ist sie ein schwebender Knopf über der Leiste, immer da, ohne Reiterwechsel. Wer keine
 * persönliche Welt hat, bekommt dort „+ Aufgabe" – ein Knopf, der ins Leere führt, wäre
 * schlimmer als keiner (dieselbe Regel wie am Rechner).
 *
 * ## Warum der Reiter gemerkt wird
 *
 * Wer morgens „Vorlauf" ansieht, sieht ihn beim nächsten Öffnen wieder – je Gerät, im
 * `localStorage`, wie der Modus-Umschalter am Rechner (`modus.ts`). Der Rechner kennt diesen
 * Merker nicht; dort gibt es keine Reiter.
 */
export const TELEFON_REITER = ['jetzt', 'vorlauf', 'erledigt', 'nachrichten'] as const
export type TelefonReiter = (typeof TELEFON_REITER)[number]

export function useTelefonReiter(): [TelefonReiter, (reiter: TelefonReiter) => void] {
  return useMerker<TelefonReiter>('meintag-telefon-reiter', 'jetzt', TELEFON_REITER)
}

export function MeinTagTelefon({
  lage,
  laufend,
  nachVorn,
  vorhabenOeffnen,
  ziehenOeffnen,
  starten,
  gelesenSetzen,
  schnellOeffnen,
  anlegenOeffnen,
}: {
  lage: MeinTagLage
  /** Die laufenden Aufgaben in Ansichtsreihenfolge – „nach vorn" ist schon eingerechnet. */
  laufend: readonly Aufgabe[]
  nachVorn: (id: string) => void
  vorhabenOeffnen: (id: string) => void
  /** Öffnet das Fenster „Was als Nächstes". */
  ziehenOeffnen: () => void
  starten: (id: string) => void
  gelesenSetzen: (v: { taskId: string; bis: string; gelesen: boolean }) => void
  /** `null`, wenn es keine persönliche Welt gibt – dann trägt der Knopf „+ Aufgabe". */
  schnellOeffnen: (() => void) | null
  anlegenOeffnen: () => void
}) {
  const [reiter, reiterMerken] = useTelefonReiter()
  /**
   * **Ein Reiterwechsel beginnt oben.** Wer im Vorlauf weit unten war und „Erledigt" tippt,
   * landete sonst mitten in der neuen Liste – die Seite scrollt, nicht der Reiter.
   */
  const setReiter = (neu: TelefonReiter) => {
    reiterMerken(neu)
    window.scrollTo({ top: 0 })
  }

  /**
   * Dieselbe Abfrage wie in `Nachrichten` – TanStack Query führt sie einmal. Hier nur für die
   * Zahl am Reiter: sie soll dastehen, **bevor** jemand den Reiter öffnet, sonst sagt sie nichts.
   */
  const { data: feed = [] } = useQuery(aktivitaetsfeedQuery(14))
  const takt = useNachlaufTakt(feed)
  const nachrichten = nachrichtenGruppen(feed, takt)
  const ungelesen = nachrichten.erwaehnt.length + nachrichten.antworten.length

  /** Wie am Rechner im Modus „Einfach": Kommentare liegen bei den Nachrichten (E-270). */
  const klopft = lage.klopft.filter((m) => m.art === 'frist' || m.art === 'abnahme')

  const reiterListe: {
    id: TelefonReiter
    text: string
    Symbol: ComponentType<{ className?: string }>
    zahl: number
    ton: 'rot' | 'grau' | 'gruen' | 'akzent'
  }[] = [
    { id: 'jetzt', text: 'Jetzt', Symbol: CircleDot, zahl: klopft.length, ton: 'rot' },
    {
      id: 'vorlauf',
      text: 'Vorlauf',
      Symbol: ListOrdered,
      zahl: lage.naechstes.length,
      ton: 'grau',
    },
    {
      id: 'erledigt',
      text: 'Erledigt',
      Symbol: CircleCheckBig,
      zahl: lage.heuteFertig,
      ton: 'gruen',
    },
    {
      id: 'nachrichten',
      text: 'Nachrichten',
      Symbol: MessageSquare,
      zahl: ungelesen,
      ton: 'akzent',
    },
  ]

  return (
    <>
      <div
        role="tabpanel"
        id={`telefon-${reiter}`}
        aria-labelledby={`reiter-${reiter}`}
        className="flex flex-col gap-[18px]"
      >
        {reiter === 'jetzt' && (
          <>
            <KlopftAn meldungen={klopft} ziehen={starten} gelesenSetzen={gelesenSetzen} />
            <Jetzt
              laufend={laufend}
              wip={lage.wip}
              wipLimit={lage.wipLimit}
              wipBereich={lage.wipBereich}
              vorhabenInfo={lage.vorhabenInfo}
              nachVorn={nachVorn}
              vorhabenOeffnen={vorhabenOeffnen}
              ziehen={ziehenOeffnen}
            />
          </>
        )}
        {reiter === 'vorlauf' && (
          /** Z-20: Tagstreifen und „Auswählen" um den Vorlauf – markieren ohne Ziehen. */
          <TelefonVorlauf rangfolge={lage.naechstes}>
            <Vorlauf
              rangfolge={lage.naechstes}
              wip={lage.wip}
              wipLimit={lage.wipLimit}
              fokusFuer={lage.fokusFuer}
              wipHinweisText={lage.wipHinweisText}
              hinweis={lage.zuvielHinweis}
              fensterOeffnen={ziehenOeffnen}
              naechstesOben
            />
          </TelefonVorlauf>
        )}
        {reiter === 'erledigt' && <Geschafft verlauf={lage.verlauf} einfach />}
        {reiter === 'nachrichten' && (
          <>
            <Nachrichten />
            <AufDemSchirm
              angepinnt={lage.angepinnt}
              warte={lage.warte}
              markierungen={lage.markierungen}
              schlafend={lage.schlafend}
            />
            <RuhigGestellt
              wartet={lage.wartet}
              laengstesWarten={lage.laengstesWarten}
              vorratGesamt={lage.vorratGesamt}
              bestandGesamt={lage.bestandGesamt}
              neuFuerDich={lage.neuFuerDich}
              ohneFeed
            />
          </>
        )}
      </div>

      {/* Platz für Leiste und ⚡-Knopf, damit die letzte Zeile nicht darunter verschwindet. */}
      <div aria-hidden="true" className="h-[132px]" />

      {/**
       * **Der Daumenknopf.** Über der Leiste, rechts, wo die Hand ohnehin liegt. `bottom` rechnet
       * die Leistenhöhe und den Sicherheitsabstand des Geräts ein – auf einem iPhone ohne
       * Home-Taste liegt darunter die Wischkante.
       */}
      <DaumenKnopf schnellOeffnen={schnellOeffnen} anlegenOeffnen={anlegenOeffnen} ueberLeiste />

      <nav
        aria-label="Mein Tag – Reiter"
        className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur"
      >
        <div role="tablist" className="grid grid-cols-4">
          {reiterListe.map(({ id, text, Symbol, zahl, ton }) => {
            const aktiv = reiter === id
            return (
              <button
                key={id}
                type="button"
                role="tab"
                id={`reiter-${id}`}
                aria-selected={aktiv}
                aria-controls={`telefon-${id}`}
                /* Die Zahl gehört in den Namen, nicht davor: „Vorlauf (7)" statt „7Vorlauf". */
                aria-label={zahl > 0 ? `${text} (${zahl})` : text}
                onClick={() => setReiter(id)}
                className={cn(
                  'flex min-h-14 cursor-pointer flex-col items-center justify-center gap-[3px] text-[10.5px] font-semibold',
                  aktiv ? 'text-accent-deep' : 'text-ink2',
                )}
              >
                <span className="relative">
                  <Symbol className="size-[22px]" />
                  {zahl > 0 && (
                    <span
                      aria-hidden="true"
                      className={cn(
                        'tabular absolute -top-1.5 -right-2.5 min-w-4 rounded-full px-1 text-center text-[9.5px] leading-4 font-bold',
                        ton === 'rot' && 'bg-pool-sofort text-white',
                        ton === 'grau' && 'bg-[#eeedea] text-ink2',
                        ton === 'gruen' && 'bg-good text-white',
                        ton === 'akzent' && 'bg-accent text-white',
                      )}
                    >
                      {zahl}
                    </span>
                  )}
                </span>
                {text}
              </button>
            )
          })}
        </div>
      </nav>
    </>
  )
}

/**
 * **Der ⚡-Knopf, in jeder Ansicht des Telefons** (15.10.2026). Nils: *„Mobil ist in der
 * Kalenderansicht keine schnellaufgabe möglich."* Er hing nur an der Liste; Kalender und Circle
 * ersetzen auf dem Telefon die ganze Ansicht und hatten deshalb keinen.
 *
 * `ueberLeiste`: in der Liste sitzt er über der Reiterleiste, sonst am unteren Rand.
 */
export function DaumenKnopf({
  schnellOeffnen,
  anlegenOeffnen,
  ueberLeiste = false,
}: {
  schnellOeffnen: (() => void) | null
  anlegenOeffnen: () => void
  ueberLeiste?: boolean
}) {
  return (
    <button
      type="button"
      onClick={schnellOeffnen ?? anlegenOeffnen}
      aria-label={schnellOeffnen ? 'Schnelle Aufgabe' : 'Aufgabe anlegen'}
      className={cn(
        'fixed right-4 z-30 flex size-14 cursor-pointer items-center justify-center rounded-full bg-accent text-white shadow-[0_6px_20px_rgb(42_120_214/0.45)] active:brightness-95',
        ueberLeiste
          ? 'bottom-[calc(76px+env(safe-area-inset-bottom))]'
          : 'bottom-[calc(20px+env(safe-area-inset-bottom))]',
      )}
    >
      {schnellOeffnen ? <Zap className="size-6" /> : <Plus className="size-6" />}
    </button>
  )
}
